// src/pages/admin/ver-taller-individual.tsx
// REFACTORIZADO: Usa el hook useTaller y componentes de loading-states

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import { QRCodeCanvas } from 'qrcode.react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Importar el hook y tipos
import { useTaller, Inscripcion, InscripcionExterna } from '../../hooks/useTaller';

// Importar componentes de loading y PageTitle
import { LoadingMessage, ErrorState, EmptyState, SkeletonTable } from '../../components/loading-states';
import PageTitle from '../../components/page-title';

export default function VerTallerIndividual() {
  const { id } = useParams();
  const location = useLocation();
  const inscritosRef = useRef<HTMLDivElement>(null);

  // ========== USAR EL HOOK useTaller ==========
  const {
    taller,
    loading,
    error,
    fetchTallerById,
    fetchInscripciones,
    formatearCosto
  } = useTaller();

  // Estados locales para inscritos
  const [inscritos, setInscritos] = useState<Inscripcion[]>([]);
  const [inscritosExternos, setInscritosExternos] = useState<InscripcionExterna[]>([]);
  const [editando, setEditando] = useState<{ [key: string]: boolean }>({});
  const [nombresPerfiles, setNombresPerfiles] = useState<{ [id: string]: string }>({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingInscritos, setLoadingInscritos] = useState(true);

  // Estados para filtros de inscritos con cuenta
  const [filtroNombreInsc, setFiltroNombreInsc] = useState('');
  const [filtroPagoInsc, setFiltroPagoInsc] = useState<'todos' | 'pendiente' | 'completado'>('todos');
  const [filtroAsistenciaInsc, setFiltroAsistenciaInsc] = useState<'todos' | 'si' | 'no'>('todos');
  const [filtroCompletadoInsc, setFiltroCompletadoInsc] = useState<'todos' | 'si' | 'no'>('todos');

  // Estados para filtros de inscritos externos
  const [filtroNombreExt, setFiltroNombreExt] = useState('');
  const [filtroPagoExt, setFiltroPagoExt] = useState<'todos' | 'pendiente' | 'completado'>('todos');
  const [filtroAsistenciaExt, setFiltroAsistenciaExt] = useState<'todos' | 'si' | 'no'>('todos');
  const [filtroCompletadoExt, setFiltroCompletadoExt] = useState<'todos' | 'si' | 'no'>('todos');

  // Estado para mostrar QR modal
  const [qrModal, setQrModal] = useState<{ visible: boolean; codigo: string; nombre: string }>({
    visible: false,
    codigo: '',
    nombre: ''
  });

  // Cargar taller
  useEffect(() => {
    if (id) {
      fetchTallerById(id);
    }
  }, [id, fetchTallerById]);

  // Cargar inscritos cuando el taller esté listo
  useEffect(() => {
    const cargarInscritos = async () => {
      if (!id || !taller) return;

      setLoadingInscritos(true);

      try {
        // Usar la función del hook para obtener inscripciones
        const { internos, externos } = await fetchInscripciones(id);
        setInscritos(internos);
        setInscritosExternos(externos);

        // Buscar nombres por perfil_id
        const perfilIdsSet = new Set<string>();
        internos.forEach(i => {
          if (i.perfil_id) perfilIdsSet.add(i.perfil_id);
        });
        const perfilIds = Array.from(perfilIdsSet);
        const nombres: { [id: string]: string } = {};

        for (const perfilId of perfilIds) {
          // Buscar en asesor (AFILIADO)
          let docRef = doc(db, 'asesor', perfilId);
          let snap = await getDoc(docRef);
          if (snap.exists()) {
            nombres[perfilId] = snap.data().nombre || 'Sin nombre';
            continue;
          }

          // Buscar en asociado (ASOCIADO)
          docRef = doc(db, 'asociado', perfilId);
          snap = await getDoc(docRef);
          if (snap.exists()) {
            nombres[perfilId] = snap.data().nombre || 'Sin nombre';
            continue;
          }

          // Buscar en admin (ADMIN)
          docRef = doc(db, 'admin', perfilId);
          snap = await getDoc(docRef);
          if (snap.exists()) {
            nombres[perfilId] = snap.data().nombre || 'Sin nombre';
            continue;
          }

          nombres[perfilId] = `ID: ${perfilId}`;
        }

        setNombresPerfiles(nombres);
        setDataLoaded(true);
      } finally {
        setLoadingInscritos(false);
      }
    };

    cargarInscritos();
  }, [id, taller, fetchInscripciones]);

  // Efecto para hacer scroll a la sección de inscritos si viene con #inscritos
  useEffect(() => {
    if (dataLoaded && location.hash === '#inscritos' && inscritosRef.current) {
      setTimeout(() => {
        inscritosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [dataLoaded, location.hash]);

  const handleFieldChange = (inscripcionId: string, field: string, value: any) => {
    setInscritos(prev =>
      prev.map(insc => (insc.id === inscripcionId ? { ...insc, [field]: value } : insc))
    );
    setInscritosExternos(prev =>
      prev.map(insc => (insc.id === inscripcionId ? { ...insc, [field]: value } : insc))
    );
  };

  const handleGuardar = async (inscripcionId: string, esExterno = false) => {
    const insc = esExterno
      ? inscritosExternos.find(i => i.id === inscripcionId)
      : inscritos.find(i => i.id === inscripcionId);
    if (!insc) return;

    const asistencia = insc.asistencia ?? false;
    const estatus_pago = insc.estatus_pago ?? 'pendiente';
    const completado = estatus_pago === 'completado' && asistencia === true;

    await updateDoc(
      doc(db, esExterno ? 'externo_inscripcion' : 'inscripcion', inscripcionId),
      { estatus_pago, asistencia, completado }
    );

    if (esExterno) {
      setInscritosExternos(prev =>
        prev.map(i => (i.id === inscripcionId ? { ...i, completado } : i))
      );
    } else {
      setInscritos(prev =>
        prev.map(i => (i.id === inscripcionId ? { ...i, completado } : i))
      );
    }

    setEditando(prev => ({ ...prev, [inscripcionId]: false }));
  };

  const handleEditar = (inscripcionId: string) => {
    setEditando(prev => ({ ...prev, [inscripcionId]: true }));
  };

  // Filtrar inscritos con cuenta
  const inscritosFiltrados = inscritos.filter(insc => {
    const nombre = nombresPerfiles[insc.perfil_id] || insc.perfil_id;
    if (filtroNombreInsc && !nombre.toLowerCase().includes(filtroNombreInsc.toLowerCase())) return false;
    if (filtroPagoInsc !== 'todos' && insc.estatus_pago !== filtroPagoInsc) return false;
    if (filtroAsistenciaInsc === 'si' && !insc.asistencia) return false;
    if (filtroAsistenciaInsc === 'no' && insc.asistencia) return false;
    if (filtroCompletadoInsc === 'si' && !insc.completado) return false;
    if (filtroCompletadoInsc === 'no' && insc.completado) return false;
    return true;
  });

  // Filtrar inscritos externos
  const inscritosExternosFiltrados = inscritosExternos.filter(insc => {
    if (filtroNombreExt && !insc.nombre.toLowerCase().includes(filtroNombreExt.toLowerCase())) return false;
    if (filtroPagoExt !== 'todos' && insc.estatus_pago !== filtroPagoExt) return false;
    if (filtroAsistenciaExt === 'si' && !insc.asistencia) return false;
    if (filtroAsistenciaExt === 'no' && insc.asistencia) return false;
    if (filtroCompletadoExt === 'si' && !insc.completado) return false;
    if (filtroCompletadoExt === 'no' && insc.completado) return false;
    return true;
  });

  const exportarExcel = () => {
    const data = [
      ...inscritos.map(i => ({
        nombre: nombresPerfiles[i.perfil_id] || i.perfil_id,
        tipo: i.tipo_perfil,
        fecha: i.fechaInscripcion?.toDate?.().toLocaleDateString('es-MX'),
        pago: i.estatus_pago,
        asistencia: i.asistencia ? 'Sí' : 'No',
        completado: i.completado ? 'Sí' : 'No'
      })),
      ...inscritosExternos.map(i => ({
        nombre: i.nombre,
        correo: i.correo,
        telefono: i.telefono,
        inmobiliaria: i.inmobiliaria,
        fecha: i.fechaInscripcion?.toDate?.().toLocaleDateString('es-MX'),
        pago: i.estatus_pago,
        asistencia: i.asistencia ? 'Sí' : 'No',
        completado: i.completado ? 'Sí' : 'No'
      }))
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inscritos');
    const blob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
      type: 'application/octet-stream'
    });
    saveAs(blob, `inscritos_taller_${taller?.nombre || 'export'}.xlsx`);
  };

  // ========== RENDER ==========

  // Estado de carga
  if (loading) {
    return (
      <>
        <UserNav />
        <PageTitle title="Detalles del Taller" subtitle="Cargando información" />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Cargando taller..." subMessage="Por favor espera" />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de error
  if (error) {
    return (
      <>
        <UserNav />
        <PageTitle title="Detalles del Taller" subtitle="Error" />
        <section className="gray-simple">
          <div className="container py-5">
            <ErrorState
              title="Error al cargar taller"
              message={error}
              onRetry={() => id && fetchTallerById(id)}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Taller no encontrado
  if (!taller) {
    return (
      <>
        <UserNav />
        <PageTitle title="Detalles del Taller" subtitle="No encontrado" />
        <section className="gray-simple">
          <div className="container py-5">
            <EmptyState
              icon="fa-solid fa-circle-exclamation"
              title="Taller no encontrado"
              message="El taller solicitado no existe o fue eliminado."
              actionText="Ver todos los talleres"
              onAction={() => window.location.href = '/admin-talleres'}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  const fecha = taller.fecha?.toDate?.() || taller.fechaInicio?.toDate?.();

  return (
    <>
      <UserNav />
      <PageTitle
        title={taller.nombre}
        subtitle="Administración del taller"
      />

      <div className="container mt-4">
        {/* Header con estado */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0 visually-hidden">{taller.nombre}</h1>
          {taller.estadoCalculado === 'proximo' && (
            <span className="badge bg-primary fs-5">
              <i className="fa-solid fa-calendar me-1" aria-hidden="true"></i> Próximo
            </span>
          )}
          {taller.estadoCalculado === 'en_curso' && (
            <span className="badge bg-success fs-5">
              <i className="fa-solid fa-play me-1" aria-hidden="true"></i> En Curso
            </span>
          )}
          {taller.estadoCalculado === 'finalizado' && (
            <span className="badge bg-secondary fs-5">
              <i className="fa-solid fa-check me-1" aria-hidden="true"></i> Finalizado
            </span>
          )}
          {taller.estadoCalculado === 'cancelado' && (
            <span className="badge bg-danger fs-5">
              <i className="fa-solid fa-times me-1" aria-hidden="true"></i> Cancelado
            </span>
          )}
          {!taller.estadoCalculado && (
            <span className="badge bg-info fs-5">
              <i className="fa-solid fa-question me-1" aria-hidden="true"></i> Sin Estado
            </span>
          )}
        </div>

        {/* Banner */}
        {taller.bannerUrl && (
          <img
            src={taller.bannerUrl}
            alt={`Banner del taller ${taller.nombre}`}
            className="img-fluid rounded mb-4 shadow"
            style={{ maxHeight: '300px', objectFit: 'cover', width: '100%' }}
          />
        )}

        {/* Cards de información */}
        <div className="row g-4">
          {/* Card: Información del Evento */}
          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa-solid fa-calendar-days me-2" aria-hidden="true"></i>
                  Información del Evento
                </h5>
              </div>
              <div className="card-body">
                <p className="mb-2">
                  <span className="badge bg-secondary me-2">Fecha</span>
                  {fecha?.toLocaleDateString('es-MX') || 'Por definir'}
                </p>
                <p className="mb-2">
                  <span className="badge bg-secondary me-2">Horario</span>
                  {taller.horaInicio && taller.horaTermino
                    ? `${taller.horaInicio} - ${taller.horaTermino}`
                    : taller.horario || 'Por definir'}
                </p>
                <p className="mb-2">
                  <span className="badge bg-secondary me-2">Lugar</span>
                  {taller.lugar}
                </p>
                {taller.direccion && (
                  <p className="mb-2">
                    <span className="badge bg-secondary me-2">Dirección</span>
                    {taller.direccion}{taller.ciudad ? `, ${taller.ciudad}` : ''}
                  </p>
                )}
                {taller.tipoEvento && (
                  <p className="mb-2">
                    <span className="badge bg-info me-2">Modalidad</span>
                    {taller.tipoEvento}
                  </p>
                )}
                <p className="mb-0">
                  <span className="badge bg-secondary me-2">Cupo</span>
                  {taller.cupoMaximo || taller.cupo || 'Ilimitado'}
                </p>
              </div>
            </div>
          </div>

          {/* Card: Organización */}
          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                  <i className="fa-solid fa-users me-2" aria-hidden="true"></i>
                  Organización
                </h5>
              </div>
              <div className="card-body">
                <p className="mb-2">
                  <span className="badge bg-secondary me-2">Organizador</span>
                  {taller.organizadorPrincipal || taller.expositor || 'AMPI'}
                </p>
                {taller.responsable && (
                  <>
                    <p className="mb-2">
                      <span className="badge bg-secondary me-2">Responsable</span>
                      {taller.responsable.nombre} ({taller.responsable.cargo})
                    </p>
                    {taller.responsable.correo && (
                      <p className="mb-2">
                        <span className="badge bg-secondary me-2">Email</span>
                        {taller.responsable.correo}
                      </p>
                    )}
                    {taller.responsable.telefono && (
                      <p className="mb-0">
                        <span className="badge bg-secondary me-2">Teléfono</span>
                        {taller.responsable.telefono}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Card: Detalles del Evento */}
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-warning">
                <h5 className="mb-0">
                  <i className="fa-solid fa-circle-info me-2" aria-hidden="true"></i>
                  Detalles del Evento
                </h5>
              </div>
              <div className="card-body">
                {taller.objetivo && (
                  <p className="mb-2">
                    <span className="badge bg-secondary me-2">Objetivo</span>
                    {taller.objetivo}
                  </p>
                )}
                {taller.publicoObjetivo && (
                  <p className="mb-2">
                    <span className="badge bg-secondary me-2">Dirigido a</span>
                    {taller.publicoObjetivo}
                  </p>
                )}
                {taller.descripcionGeneral && (
                  <p className="mb-2">
                    <span className="badge bg-secondary me-2">Descripción</span>
                    {taller.descripcionGeneral}
                  </p>
                )}

                <div className="d-flex gap-3 flex-wrap mt-3">
                  <span className={`badge ${taller.tipoCosto === 'gratuito' ? 'bg-success' : 'bg-primary'} fs-6`}>
                    {formatearCosto(taller)}
                  </span>
                  {(taller.creditos || 0) > 0 && (
                    <span className="badge bg-info fs-6">
                      <i className="fa-solid fa-certificate me-1" aria-hidden="true"></i>
                      {taller.creditos} Créditos AMPI
                    </span>
                  )}
                </div>

                {taller.actividades && taller.actividades.length > 0 && (
                  <div className="mt-3">
                    <strong>Actividades:</strong>
                    <ul className="mt-2">
                      {taller.actividades.map((act: string, idx: number) => (
                        <li key={idx}>{act}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <hr className="my-4" />

        {/* Sección de Inscritos */}
        <div ref={inscritosRef} id="inscritos" className="d-flex justify-content-between align-items-center mb-4 pt-3">
          <div>
            <h3>
              <i className="fa-solid fa-list-check me-2" aria-hidden="true"></i>
              Lista de Inscritos
            </h3>
            <p className="text-muted">
              Total inscritos: {inscritos.length + inscritosExternos.length}
              ({inscritos.length} con cuenta, {inscritosExternos.length} externos)
            </p>
          </div>
          <button
            className="btn btn-success"
            onClick={exportarExcel}
            disabled={inscritos.length === 0 && inscritosExternos.length === 0}
            aria-label="Exportar lista de inscritos a Excel"
          >
            <i className="fa-solid fa-file-excel me-2" aria-hidden="true"></i>
            Exportar a Excel
          </button>
        </div>

        {/* Inscritos con Cuenta */}
        <h4 className="mt-4">
          <i className="fa-solid fa-user-check me-2" aria-hidden="true"></i>
          Inscritos con Cuenta
        </h4>

        {/* Filtros para inscritos con cuenta */}
        <div className="card mb-3 p-3 shadow-sm">
          <h6 className="mb-3">
            <i className="fa-solid fa-filter me-2" aria-hidden="true"></i>
            Filtros
          </h6>
          <div className="row g-2">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por nombre..."
                value={filtroNombreInsc}
                onChange={(e) => setFiltroNombreInsc(e.target.value)}
                aria-label="Filtrar por nombre"
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroPagoInsc}
                onChange={(e) => setFiltroPagoInsc(e.target.value as any)}
                aria-label="Filtrar por estado de pago"
              >
                <option value="todos">Todos los pagos</option>
                <option value="pendiente">Pendiente</option>
                <option value="completado">Completado</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroAsistenciaInsc}
                onChange={(e) => setFiltroAsistenciaInsc(e.target.value as any)}
                aria-label="Filtrar por asistencia"
              >
                <option value="todos">Todas las asistencias</option>
                <option value="si">Con asistencia</option>
                <option value="no">Sin asistencia</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroCompletadoInsc}
                onChange={(e) => setFiltroCompletadoInsc(e.target.value as any)}
                aria-label="Filtrar por estado completado"
              >
                <option value="todos">Todos</option>
                <option value="si">Completados</option>
                <option value="no">No completados</option>
              </select>
            </div>
          </div>
          <div className="mt-2 text-end">
            <span className="text-muted small">
              Mostrando {inscritosFiltrados.length} de {inscritos.length} inscritos
            </span>
          </div>
        </div>

        {loadingInscritos ? (
          <SkeletonTable rows={5} columns={8} />
        ) : inscritos.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover mt-3">
              <thead className="table-light">
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Pago</th>
                  <th>Asistencia</th>
                  <th>Completado</th>
                  <th>QR</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {inscritosFiltrados.map(insc => (
                  <tr key={insc.id}>
                    <td>{nombresPerfiles[insc.perfil_id] || insc.perfil_id}</td>
                    <td>{insc.tipo_perfil}</td>
                    <td>{insc.fechaInscripcion?.toDate?.().toLocaleDateString('es-MX')}</td>
                    <td>
                      <select
                        value={insc.estatus_pago}
                        disabled={!editando[insc.id as string]}
                        onChange={e => handleFieldChange(insc.id as string, 'estatus_pago', e.target.value)}
                        className="form-select form-select-sm"
                        aria-label="Estado de pago"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="completado">Completado</option>
                      </select>
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        disabled={!editando[insc.id as string]}
                        checked={insc.asistencia || false}
                        onChange={e => handleFieldChange(insc.id as string, 'asistencia', e.target.checked)}
                        className="form-check-input"
                        aria-label="Asistencia"
                      />
                    </td>
                    <td>
                      <span className={`badge ${insc.completado ? 'bg-success' : 'bg-secondary'}`}>
                        {insc.completado ? 'Completado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="text-center">
                      {insc.codigo_qr ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setQrModal({
                            visible: true,
                            codigo: insc.codigo_qr!,
                            nombre: nombresPerfiles[insc.perfil_id] || insc.perfil_id
                          })}
                          aria-label="Ver código QR"
                        >
                          <i className="fa-solid fa-eye" aria-hidden="true"></i>
                        </button>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {editando[insc.id as string] ? (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleGuardar(insc.id as string)}
                          aria-label="Guardar cambios"
                        >
                          <i className="fa-solid fa-save me-1" aria-hidden="true"></i>
                          Guardar
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditar(insc.id as string)}
                          aria-label="Editar inscripción"
                        >
                          <i className="fa-solid fa-edit me-1" aria-hidden="true"></i>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon="fa-solid fa-user-slash"
            title="Sin inscritos con cuenta"
            message="Aún no hay usuarios registrados inscritos en este taller."
          />
        )}

        <hr className="my-5" />

        {/* Inscritos Externos */}
        <h4 className="mt-4">
          <i className="fa-solid fa-user-plus me-2" aria-hidden="true"></i>
          Inscritos Externos
        </h4>

        {/* Filtros para inscritos externos */}
        <div className="card mb-3 p-3 shadow-sm">
          <h6 className="mb-3">
            <i className="fa-solid fa-filter me-2" aria-hidden="true"></i>
            Filtros
          </h6>
          <div className="row g-2">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por nombre..."
                value={filtroNombreExt}
                onChange={(e) => setFiltroNombreExt(e.target.value)}
                aria-label="Filtrar externos por nombre"
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroPagoExt}
                onChange={(e) => setFiltroPagoExt(e.target.value as any)}
                aria-label="Filtrar externos por pago"
              >
                <option value="todos">Todos los pagos</option>
                <option value="pendiente">Pendiente</option>
                <option value="completado">Completado</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroAsistenciaExt}
                onChange={(e) => setFiltroAsistenciaExt(e.target.value as any)}
                aria-label="Filtrar externos por asistencia"
              >
                <option value="todos">Todas las asistencias</option>
                <option value="si">Con asistencia</option>
                <option value="no">Sin asistencia</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filtroCompletadoExt}
                onChange={(e) => setFiltroCompletadoExt(e.target.value as any)}
                aria-label="Filtrar externos por completado"
              >
                <option value="todos">Todos</option>
                <option value="si">Completados</option>
                <option value="no">No completados</option>
              </select>
            </div>
          </div>
          <div className="mt-2 text-end">
            <span className="text-muted small">
              Mostrando {inscritosExternosFiltrados.length} de {inscritosExternos.length} externos
            </span>
          </div>
        </div>

        {loadingInscritos ? (
          <SkeletonTable rows={5} columns={10} />
        ) : inscritosExternos.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover mt-3">
              <thead className="table-light">
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Teléfono</th>
                  <th>Inmobiliaria</th>
                  <th>Fecha</th>
                  <th>Pago</th>
                  <th>Asistencia</th>
                  <th>Completado</th>
                  <th>QR</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {inscritosExternosFiltrados.map(insc => (
                  <tr key={insc.id}>
                    <td>{insc.nombre}</td>
                    <td>{insc.correo}</td>
                    <td>{insc.telefono}</td>
                    <td>{insc.inmobiliaria}</td>
                    <td>{insc.fechaInscripcion?.toDate?.().toLocaleDateString('es-MX')}</td>
                    <td>
                      <select
                        value={insc.estatus_pago}
                        disabled={!editando[insc.id as string]}
                        onChange={e => handleFieldChange(insc.id as string, 'estatus_pago', e.target.value)}
                        className="form-select form-select-sm"
                        aria-label="Estado de pago"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="completado">Completado</option>
                      </select>
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        disabled={!editando[insc.id as string]}
                        checked={insc.asistencia || false}
                        onChange={e => handleFieldChange(insc.id as string, 'asistencia', e.target.checked)}
                        className="form-check-input"
                        aria-label="Asistencia"
                      />
                    </td>
                    <td>
                      <span className={`badge ${insc.completado ? 'bg-success' : 'bg-secondary'}`}>
                        {insc.completado ? 'Completado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="text-center">
                      {insc.codigo_qr ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setQrModal({ visible: true, codigo: insc.codigo_qr!, nombre: insc.nombre })}
                          aria-label="Ver código QR"
                        >
                          <i className="fa-solid fa-eye" aria-hidden="true"></i>
                        </button>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {editando[insc.id as string] ? (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleGuardar(insc.id as string, true)}
                          aria-label="Guardar cambios"
                        >
                          <i className="fa-solid fa-save me-1" aria-hidden="true"></i>
                          Guardar
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditar(insc.id as string)}
                          aria-label="Editar inscripción"
                        >
                          <i className="fa-solid fa-edit me-1" aria-hidden="true"></i>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon="fa-solid fa-user-slash"
            title="Sin inscritos externos"
            message="Aún no hay participantes externos inscritos en este taller."
          />
        )}

        {/* Modal para mostrar QR */}
        {qrModal.visible && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-modal-title"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="qr-modal-title">
                    Código QR - {qrModal.nombre}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setQrModal({ visible: false, codigo: '', nombre: '' })}
                    aria-label="Cerrar modal"
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <QRCodeCanvas value={qrModal.codigo} size={256} />
                  <p className="mt-3 text-muted">Escanea este código para validar asistencia</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setQrModal({ visible: false, codigo: '', nombre: '' })}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
